import Markdown from "react-markdown";
import * as ReactRouter from "react-router";
import { t as defaultTranslate } from "~/app/i18n";
import { cn } from "~/app/lib/utils";
import * as Paths from "~/app/paths";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";

type ReportTranslationKey = Parameters<typeof defaultTranslate>[0];
type ReportTranslate = (
  key: ReportTranslationKey,
  params?: Record<string, string>
) => string;

export type SejmMonitorReportItemView = {
  id: string;
  monitorPhrase: string;
  matchField?: string;
  matchExcerpt?: string | null;
  isNew: boolean;
  actId: string;
  actTitle: string | null;
  actDisplayAddress: string | null;
  actPublisherCode: string;
  actYear: number;
  actPos: number;
  actAnnouncementDate: string | null;
  actChangeDate: string | null;
  actEntryIntoForce: string | null;
  actExpirationDate: string | null;
  actLegalStatusDate: string | null;
  actPromulgation: string | null;
  actRepealDate: string | null;
  actValidFrom: string | null;
  actStatus: string | null;
  actInForce: string | null;
  processNote: string | null;
  processLink: string | null;
};

export type SejmMonitorReportSummaryItemView = {
  changeSummary: string;
  provisionName: string;
  officialSourceUrl: string;
  actDetails: {
    title: string | null;
    citation: string;
    actPublisherCode: string;
    actYear: number;
    actPos: number;
    eventDate: string;
    eventKind: "announcement" | "change";
    changeDate: string | null;
    announcementDate: string | null;
    entryIntoForce: string | null;
    expirationDate: string | null;
    legalStatusDate: string | null;
    promulgation: string | null;
    repealDate: string | null;
    validFrom: string | null;
    actStatus: string | null;
    actInForce: string | null;
    processNote: string | null;
  };
};

export type SejmMonitorReportDetailData = {
  id: string;
  monitorId: string;
  monitorPhrase: string;
  windowStart: string;
  windowEnd: string;
  sourceActCount: number;
  matchedActCount: number;
  matchedItemCount: number;
  createdAt: string;
  summaryMarkdown: string;
  summaryItems: SejmMonitorReportSummaryItemView[];
  items: SejmMonitorReportItemView[];
};

function ReportMetric({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

export function formatDateRange(start: string | Date, end: string | Date) {
  const startDate = typeof start === "string" ? new Date(start) : start;
  const endDate = typeof end === "string" ? new Date(end) : end;

  const startLabel = startDate.toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const endLabel = endDate.toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
}

function formatLegalDate(value: string) {
  const dateOnlyMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
  if (dateOnlyMatch) {
    return dateOnlyMatch[0];
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  return value;
}

function DateFact({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <span>
      {label}: {formatLegalDate(value)}
    </span>
  );
}

function getReportWindowEvent(
  report: Pick<SejmMonitorReportDetailData, "windowStart" | "windowEnd">,
  item: Pick<
    SejmMonitorReportItemView,
    "actChangeDate" | "actAnnouncementDate" | "isNew"
  >,
  translate: ReportTranslate
) {
  const windowStart = new Date(report.windowStart);
  const windowEnd = new Date(report.windowEnd);

  const changeDate = item.actChangeDate ? new Date(item.actChangeDate) : null;
  if (
    changeDate &&
    !Number.isNaN(changeDate.getTime()) &&
    changeDate >= windowStart &&
    changeDate <= windowEnd
  ) {
    return {
      label: item.isNew ? translate("New act") : translate("Change date"),
      value: item.actChangeDate,
    };
  }

  const announcementDate = item.actAnnouncementDate
    ? new Date(item.actAnnouncementDate)
    : null;
  if (
    announcementDate &&
    !Number.isNaN(announcementDate.getTime()) &&
    announcementDate >= windowStart &&
    announcementDate <= windowEnd
  ) {
    return {
      label: translate("Announcement date"),
      value: item.actAnnouncementDate,
    };
  }

  return null;
}

export function SejmMonitorReportDetail({
  report,
  translate = defaultTranslate,
  className,
  showHits = true,
}: {
  report: SejmMonitorReportDetailData;
  translate?: ReportTranslate;
  className?: string;
  showHits?: boolean;
}) {
  return (
    <div className={cn("space-y-6", className)}>
      <div className="grid gap-4 md:grid-cols-2">
        <ReportMetric
          label={translate("Acts published in the selected time range")}
          value={report.sourceActCount}
        />
        <ReportMetric
          label={translate("Matched acts")}
          value={report.matchedActCount}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{report.monitorPhrase}</CardTitle>
          <CardDescription>
            {formatDateRange(report.windowStart, report.windowEnd)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {report.summaryMarkdown ? (
            <div className="mb-6 rounded-md border bg-muted/30 p-4">
              <div className="mb-3 text-sm font-medium">
                {translate("Overall synopsis")}
              </div>
              <Markdown className="prose prose-slate max-w-none text-sm leading-6 dark:prose-invert prose-p:my-2 prose-ul:my-2 prose-li:my-1">
                {report.summaryMarkdown}
              </Markdown>
            </div>
          ) : null}

          <div className="mb-4 text-sm text-muted-foreground">
            {translate("Key changes")}
          </div>
          <div className="space-y-4">
            {report.summaryItems.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {translate("This report did not contain any monitor hits.")}
              </div>
            ) : (
              report.summaryItems.map((summaryItem, index) => (
                <div
                  key={`${summaryItem.actDetails.citation}-${index}`}
                  className="space-y-3 rounded-md border p-4"
                >
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <ReactRouter.Link
                        to={Paths.eliAct(
                          summaryItem.actDetails.actPublisherCode,
                          summaryItem.actDetails.actYear,
                          summaryItem.actDetails.actPos
                        )}
                        className="block text-lg font-semibold leading-snug hover:underline"
                      >
                        {summaryItem.actDetails.title ??
                          summaryItem.actDetails.citation}
                      </ReactRouter.Link>
                      <p className="text-sm leading-6 text-muted-foreground">
                        {summaryItem.changeSummary}
                      </p>
                    </div>

                    <div className="text-sm text-muted-foreground">
                      {translate("Provision")}: {summaryItem.provisionName}
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                      <ReactRouter.Link
                        to={Paths.eliAct(
                          summaryItem.actDetails.actPublisherCode,
                          summaryItem.actDetails.actYear,
                          summaryItem.actDetails.actPos
                        )}
                        className="text-primary hover:underline"
                      >
                        {translate("Open act")}
                      </ReactRouter.Link>
                      <a
                        href={summaryItem.officialSourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        {translate("Official source")}
                      </a>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
                    <span>
                      {summaryItem.actDetails.eventKind === "announcement"
                        ? translate("Announcement date")
                        : translate("Change date")}
                      : {formatLegalDate(summaryItem.actDetails.eventDate)}
                    </span>
                    <DateFact
                      label={translate("Announcement date")}
                      value={summaryItem.actDetails.announcementDate}
                    />
                    <DateFact
                      label={translate("Entry into force")}
                      value={summaryItem.actDetails.entryIntoForce}
                    />
                    <DateFact
                      label={translate("Expiration date")}
                      value={summaryItem.actDetails.expirationDate}
                    />
                    <DateFact
                      label={translate("Legal status date")}
                      value={summaryItem.actDetails.legalStatusDate}
                    />
                    <DateFact
                      label={translate("Repeal date")}
                      value={summaryItem.actDetails.repealDate}
                    />
                    <DateFact
                      label={translate("Valid from")}
                      value={summaryItem.actDetails.validFrom}
                    />
                    <DateFact
                      label={translate("Status")}
                      value={summaryItem.actDetails.actStatus}
                    />
                    <DateFact
                      label={translate("Legislative process")}
                      value={summaryItem.actDetails.processNote}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {showHits ? (
        <div className="space-y-4">
          {report.items.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                {translate("This report did not contain any monitor hits.")}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {report.matchedItemCount} {translate("hits")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {report.items.map((item) => {
                  const reportEvent = getReportWindowEvent(
                    report,
                    item,
                    translate
                  );

                  return (
                    <div
                      key={item.id}
                      className="space-y-3 rounded-md border p-4"
                    >
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div className="space-y-1">
                          <div className="font-medium">
                            <ReactRouter.Link
                              to={Paths.eliAct(
                                item.actPublisherCode,
                                item.actYear,
                                item.actPos
                              )}
                              className="hover:underline"
                            >
                              {item.actTitle ?? translate("Untitled")}
                            </ReactRouter.Link>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {item.actDisplayAddress ??
                              `${item.actPublisherCode}/${item.actYear}/${item.actPos}`}
                          </div>
                        </div>

                        <div
                          className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-medium ${
                            item.isNew
                              ? "bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-200"
                              : "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200"
                          }`}
                        >
                          {item.isNew
                            ? translate("New act")
                            : translate("Updated act")}
                        </div>
                      </div>

                      {item.matchExcerpt ? (
                        <div className="text-sm leading-6 text-muted-foreground">
                          {item.matchExcerpt}
                        </div>
                      ) : null}

                      <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
                        {reportEvent ? (
                          <span>
                            {reportEvent.label}:{" "}
                            {reportEvent.value
                              ? formatLegalDate(reportEvent.value)
                              : reportEvent.value}
                          </span>
                        ) : null}
                        <DateFact
                          label={translate("Change date")}
                          value={item.actChangeDate}
                        />
                        <DateFact
                          label={translate("Announcement date")}
                          value={item.actAnnouncementDate}
                        />
                        <DateFact
                          label={translate("Entry into force")}
                          value={item.actEntryIntoForce}
                        />
                        <DateFact
                          label={translate("Expiration date")}
                          value={item.actExpirationDate}
                        />
                        <DateFact
                          label={translate("Legal status date")}
                          value={item.actLegalStatusDate}
                        />
                        <DateFact
                          label={translate("Repeal date")}
                          value={item.actRepealDate}
                        />
                        <DateFact
                          label={translate("Valid from")}
                          value={item.actValidFrom}
                        />
                        <DateFact
                          label={translate("Legislative process")}
                          value={item.processNote}
                        />
                      </div>

                      {item.processLink ? (
                        <a
                          href={item.processLink}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex text-sm text-primary hover:underline"
                        >
                          {translate("Open legislative process")}
                        </a>
                      ) : null}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>
      ) : null}
    </div>
  );
}
